/**
 * Pendências de exames — vw_situacao_exames.
 * Uma linha por exigência (colaborador × exame) com badge de situação:
 * pendente (nunca realizado), vencido, válido. Default: pendente + vencido.
 */
import { useState } from "react";

import { useEmpresas, usePendencias } from "@/hooks/useApi";
import type { SituacaoExame } from "@/types/api";

export const SITUACAO_BADGE: Record<SituacaoExame, { rotulo: string; classe: string }> = {
  pendente: { rotulo: "pendente", classe: "badge bg-amber-100 text-amber-800" },
  vencido: { rotulo: "vencido", classe: "badge bg-red-100 text-red-800" },
  valido: { rotulo: "válido", classe: "badge bg-emerald-100 text-emerald-800" },
};

export function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("T")[0].split("-");
  return `${d}/${m}/${a}`;
}

export function PendenciasPage() {
  const { data: empresas } = useEmpresas();
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [situacao, setSituacao] = useState<SituacaoExame | "">("");
  const [q, setQ] = useState("");

  const { data: pendencias, isLoading, error } = usePendencias({
    empresa_id: empresaId,
    situacao,
    q: q || undefined,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">Pendências</h2>
        <p className="text-sm text-ink-muted">
          Exigências do PCMSO por colaborador com a situação calculada em tempo real.
          Sem filtro de situação, mostra pendentes + vencidos.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <label className="mb-1 block text-xs font-medium text-ink-muted">Empresa</label>
          <select
            className="input"
            value={empresaId ?? ""}
            onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">todas as empresas</option>
            {(empresas ?? []).map((e) => (
              <option key={e.id} value={e.id}>{e.razao_social}</option>
            ))}
          </select>
        </div>
        <div className="w-48">
          <label className="mb-1 block text-xs font-medium text-ink-muted">Situação</label>
          <select
            className="input"
            value={situacao}
            onChange={(e) => setSituacao(e.target.value as SituacaoExame | "")}
          >
            <option value="">pendentes + vencidos</option>
            <option value="pendente">pendente</option>
            <option value="vencido">vencido</option>
            <option value="valido">válido</option>
          </select>
        </div>
        <div className="w-72">
          <label className="mb-1 block text-xs font-medium text-ink-muted">Busca</label>
          <input
            className="input"
            placeholder="colaborador ou exame…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {error ? <p className="text-sm text-anarosa">{(error as Error).message}</p> : null}
      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}

      {pendencias && pendencias.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Colaborador</th>
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Função</th>
                <th className="px-4 py-3">Exame</th>
                <th className="px-4 py-3">Periodicidade</th>
                <th className="px-4 py-3">Última realização</th>
                <th className="px-4 py-3">Vence em</th>
                <th className="px-4 py-3">Situação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pendencias.map((p, i) => (
                <tr key={`${p.colaborador_id}-${p.exame}-${i}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{p.colaborador}</td>
                  <td className="px-4 py-2 text-ink-muted">{p.empresa}</td>
                  <td className="px-4 py-2 text-ink-muted">{p.funcao}</td>
                  <td className="px-4 py-2">{p.exame}</td>
                  <td className="px-4 py-2 text-ink-muted">
                    {p.periodicidade_meses ? `${p.periodicidade_meses} meses` : "—"}
                  </td>
                  <td className="px-4 py-2 text-ink-muted">{fmtData(p.ultima_realizacao)}</td>
                  <td className="px-4 py-2 text-ink-muted">{fmtData(p.vence_em)}</td>
                  <td className="px-4 py-2">
                    <span className={SITUACAO_BADGE[p.situacao].classe}>
                      {SITUACAO_BADGE[p.situacao].rotulo}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !isLoading && !error ? (
        <p className="text-sm text-ink-muted">Nenhuma pendência encontrada com esses filtros.</p>
      ) : null}
    </div>
  );
}
